import type { SessionState } from "@/pages/admin/calls/types"

/*
 * Copy for every state and notice, in batyr's voice: plain, short, second
 * person, no exclamation marks. The pill labels are in session-pill.tsx; the
 * attention-queue reason labels are served by the backend (one vocabulary,
 * two places), with the same words here only as a fallback.
 */
export const CALL_BLOCK = {
  no_staffer_mobile:
    "Add your mobile number to your profile to call from here.",
  no_number: "There is no number for this school. Add one to call.",
  do_not_call: "This school has asked not to be called.",
  active_call: "You are already on a call.",
  outside_hours: (schoolTime: string, opensAt: string) =>
    `It is ${schoolTime} at the school. Calling opens at ${opensAt}.`,
  not_installed: "Calling arrives with Phase 3.",
} as const

export const BEFORE_YOU_DIAL = {
  instruction: "Your mobile rings first. Press 1 to place the call.",
  agendaHead: "Agenda — untick what you will not raise",
  addItem: "Add an item — one line, it becomes scoreable",
  addedTag: "added by you · scored like any ask",
  noteHead: "Note to self — primes the transcriber",
  noteHint: "Names to spell, what to avoid. The summariser reads this too.",
} as const

export const IN_CALL = {
  buttonHint:
    "End call hangs up the school and keeps you on for the memo. Cancel ends everything — no memo, no log.",
  redialing:
    "You hung up before the beep. We're calling you back for the memo. Answer, and the prompt plays again.",
  memoLost:
    "The callback wasn't answered. This call is flagged; the notes are kept and an operator can finish it.",
  memoLostAside:
    "Your agenda marks and the call record are safe. Nothing more to do here.",
  processing: "Working on the transcript. Safe to leave this page.",
  marksHead: "Agenda — mark as you talk",
  marksHeadPhone: "Tap a line when you have covered it",
  stillToCover: "Still to cover",
} as const

export const MEMO_GUIDE = {
  audio:
    "The call has ended. After the tone, record your memo. Hang up when you're finished.",
  sayWhat: "Say what happened with",
  promised: "If you promised anything, say when",
  promisedHint:
    "A date said aloud becomes a follow-up date on the review screen.",
  slowly: "Say these slowly",
  close: "Hang up when you're done.",
} as const

export const REVIEW = {
  unmentioned: "Not in the memo. Add what happened if it was discussed.",
  noMemo:
    "Nobody answered, so there is no memo and no summary. Log what happened.",
  memoLost:
    "Memo lost — the callback wasn't answered. The talk happened; the memo didn't. Your agenda marks are kept.",
  memoLostAside:
    "An operator can re-process if a recording turns up; a re-processed summary appears here.",
  secondSubmit:
    "This call is already logged. There is one Task for it — logging again would make a duplicate.",
  writesOne: "Writes one Task to Salesforce.",
  prefilled: "pre-filled from the memo",
  fromMarks: "Summary — from your marks only",
} as const

export const PRESSED_ECHO = {
  end: "Ending…",
  cancel: "Cancelling…",
  cancel_memo: "Cancelling…",
  log: "Logging…",
} as const

/** The gem's failure reasons as the queue names them; the backend serves the same map. */
export const REASON_LABELS: Record<string, string> = {
  memo_lost: "Memo lost: callback unanswered",
  recording_failed: "Recording failed: no audio saved",
  processing_failed: "Summary failed: transcript kept",
  call_unreconciled: "Call not matched to a session",
  memo_unreconciled: "Memo not matched to a call",
  stale_retry: "Retry went stale",
  aborted: "Cancelled from the card",
  aborted_during_memo: "Memo cancelled from the card",
  staffer_no_answer: "Your phone rang out",
  staffer_not_confirmed: "1 was not pressed",
  staffer_hung_up: "You hung up",
  dial_rejected: "The carrier refused the dial",
  contact_left: "The school hung up",
}

export const reasonLabel = (
  reason: string | null,
  served: string | null,
): string | null =>
  served ??
  (reason ? (REASON_LABELS[reason] ?? reason.replace(/_/g, " ")) : null)

/** The settled pills: how a call that ended short reads on the card. */
export const TERMINAL_LABELS: Partial<Record<SessionState, string>> = {
  abandoned: "Cancelled",
  interrupted: "Call dropped",
  failed: "Call failed",
}
